import { Link } from "react-router-dom";
import {
  CheckCircle2, Award, Users, ThumbsUp, Clock, ArrowRight, Shield, Star
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1759069953255-5c8f9f9912b9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const TEAM_IMG = "https://images.pexels.com/photos/6728919/pexels-photo-6728919.jpeg?auto=compress&cs=tinysrgb&w=800";
const GARDEN_IMG = "https://images.unsplash.com/photo-1704457031745-fe39ed2d296c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const whyUs = [
  {
    icon: Award,
    title: "Licensed & Insured",
    desc: "Fully licensed and insured for all services, giving you complete peace of mind.",
  },
  {
    icon: Users,
    title: "Experienced Team",
    desc: "Our skilled professionals bring years of hands-on landscaping experience to every job.",
  },
  {
    icon: ThumbsUp,
    title: "Quality Guaranteed",
    desc: "We stand behind our work with a satisfaction guarantee on every service we provide.",
  },
  {
    icon: Clock,
    title: "Reliable & Punctual",
    desc: "We show up on time, every time. Your schedule matters to us as much as it does to you.",
  },
  {
    icon: Shield,
    title: "Transparent Pricing",
    desc: "No hidden fees or surprise charges. You get a detailed quote upfront before any work begins.",
  },
  {
    icon: Star,
    title: "5-Star Reputation",
    desc: "Consistently rated 5 stars by homeowners across Mississauga and the surrounding GTA.",
  },
];

const values = [
  "Integrity in every project we take on",
  "Respect for your property and privacy",
  "Commitment to environmental responsibility",
  "Continuous improvement in our craft",
  "Building lasting relationships with clients",
  "Pride in the quality of our results",
];

export default function About() {
  return (
    <main data-testid="about-page">
      {/* Hero */}
      <section
        className="relative min-h-[45vh] flex items-center justify-center overflow-hidden"
        style={{ paddingTop: "80px" }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${HERO_BG}')` }}
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 text-center px-4 py-16">
          <span
            className="inline-block mb-3 px-4 py-1.5 bg-[#8dc63f]/20 text-[#8dc63f] text-xs font-semibold rounded-full uppercase tracking-widest border border-[#8dc63f]/30"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Our Story
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-3"
            style={{ fontFamily: "'Outfit', sans-serif", textShadow: "0 2px 20px rgba(0,0,0,0.4)" }}
          >
            About Us
          </h1>
          <p className="text-white/80 text-base mt-4 max-w-xl mx-auto">
            Dedicated to transforming outdoor spaces across Mississauga and the GTA with passion and expertise
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 md:py-28 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            {/* Images */}
            <div className="relative order-2 lg:order-1">
              <img
                src={GARDEN_IMG}
                alt="Beautifully landscaped residential property"
                className="rounded-2xl w-full h-[420px] object-cover shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 w-44 h-44 hidden md:block">
                <img
                  src={TEAM_IMG}
                  alt="Lawn care professional"
                  className="rounded-2xl w-full h-full object-cover shadow-xl border-4 border-white"
                />
              </div>
              <div
                data-testid="about-badge"
                className="absolute -top-4 -left-4 hidden md:flex flex-col items-center justify-center w-24 h-24 rounded-full shadow-xl border-4 border-white"
                style={{ background: "linear-gradient(135deg, #3a5c2e, #8dc63f)" }}
              >
                <span className="text-2xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>10+</span>
                <span className="text-white/90 text-xs text-center leading-tight">Years<br />Serving GTA</span>
              </div>
            </div>

            {/* Text */}
            <div className="order-1 lg:order-2">
              <span
                className="uppercase tracking-[0.2em] text-xs font-semibold text-[#3a5c2e] block mb-3"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Our Mission
              </span>
              <h2
                className="text-3xl sm:text-4xl font-bold text-[#1F291E] mb-6"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Transforming Outdoor Spaces, One Property at a Time
              </h2>
              <p className="text-[#4A5D48] text-base leading-relaxed mb-5">
                At Absolute Landscapers, our mission is simple: to deliver exceptional landscaping and property maintenance services that enhance the beauty, value, and functionality of your home.
              </p>
              <p className="text-[#4A5D48] text-base leading-relaxed mb-7">
                Founded in Mississauga, we've built our reputation on hard work, honesty, and an unwavering commitment to quality. Every property we touch receives the same attention and care we would give our own.
              </p>
              <h3
                className="font-semibold text-[#1F291E] mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Our Core Values
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                {values.map((v) => (
                  <li key={v} className="flex items-start gap-2 text-[#4A5D48] text-sm">
                    <CheckCircle2 size={16} className="text-[#8dc63f] mt-0.5 shrink-0" />
                    {v}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid="about-cta-btn"
                className="btn-primary px-7 py-3 rounded-xl text-sm inline-flex items-center gap-2"
              >
                Get in Touch <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-us-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span
              className="uppercase tracking-[0.2em] text-xs font-semibold text-[#3a5c2e] block mb-3"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Why Choose Us
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1F291E]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              The Absolute Landscapers Difference
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyUs.map((item) => (
              <div
                key={item.title}
                data-testid={`why-us-card-${item.title.toLowerCase().replace(/\s/g, "-").replace(/&/g, "")}`}
                className="flex gap-5 p-7 bg-[#F9F8F6] border border-[#E2E8DF] rounded-xl hover:border-[#3a5c2e]/30 hover:shadow-md transition-all duration-200"
              >
                <div className="w-11 h-11 rounded-xl bg-[#F0F4EF] flex items-center justify-center shrink-0">
                  <item.icon size={22} color="#3a5c2e" strokeWidth={1.8} />
                </div>
                <div>
                  <h3
                    className="font-semibold text-[#1F291E] mb-1.5"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-[#4A5D48] text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Manager Card */}
      <section data-testid="manager-section" className="py-16 bg-[#F0F4EF]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="bg-white rounded-2xl p-8 sm:p-12 shadow-sm border border-[#E2E8DF]">
            <div
              className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center text-white text-2xl font-bold"
              style={{ background: "linear-gradient(135deg, #3a5c2e, #8dc63f)", fontFamily: "'Outfit', sans-serif" }}
            >
              AP
            </div>
            <h3
              className="text-xl font-bold text-[#1F291E] mb-1"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Ahmad Parwiz
            </h3>
            <p className="text-[#3a5c2e] font-medium text-sm mb-4 uppercase tracking-wide">
              General Manager
            </p>
            <p className="text-[#4A5D48] text-sm leading-relaxed max-w-md mx-auto">
              "At Absolute Landscapers, we treat every client's property as if it were our own. Our team is passionate about creating beautiful, functional outdoor spaces that our clients are proud to call home."
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-16 text-center"
        style={{ background: "linear-gradient(135deg, #3a5c2e 0%, #2d4a24 100%)" }}
      >
        <div className="max-w-2xl mx-auto px-4">
          <h2
            className="text-2xl sm:text-3xl font-bold text-white mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Let's Work Together
          </h2>
          <p className="text-white/80 text-sm mb-7">
            Ready to transform your outdoor space? Get in touch for a free, no-obligation estimate.
          </p>
          <Link
            to="/contact"
            data-testid="about-footer-cta-btn"
            className="btn-accent px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2"
          >
            Contact Us Today <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
